import { useMemo, useCallback } from 'react';
import { DateTime } from 'luxon';
import {
  useWbsClientLabelSearchLazyQuery,
  WbsLabelValueSearchResult,
  WbsClientLabelSearchQueryResult,
  WbsLabelValueSearchInput,
  WbsClientLabelSearchQuery,
  WbsClientLabelSearchDocument
} from 'graphql/types';
import { FacetOption } from 'modules/notes/common';
import { SearchHandlerParams } from '../../LabelFacetDetails';
export const CLIENTS_PAGE_COUNT = 50;

const getClientOptions = (
  data: WbsClientLabelSearchQuery | undefined
): WbsLabelValueSearchResult[] =>
  data?.wbsLabelValueSearch?.clients.items.map((item) => ({
    ...item,
    id: item.id ?? null
  })) ?? [];

export type ClientLabelSearchResult = {
  options: WbsLabelValueSearchResult[];
  loading: boolean;
  searchHandler: (params: SearchHandlerParams) => void;
  hasMore: boolean;
};
export const useClientLabelSearch = (): ClientLabelSearchResult => {
  const [getClients, { data, loading }] = useWbsClientLabelSearchLazyQuery();

  const searchHandler = useCallback(
    ({ query }: SearchHandlerParams) => {
      const variables = {
        date: DateTime.local().toISODate(),
        input: {
          count: CLIENTS_PAGE_COUNT,
          query
        }
      };
      getClients({ variables });
    },
    [getClients]
  );
  const clients = useMemo(() => getClientOptions(data), [data]);
  return useMemo(
    () => ({
      searchHandler,
      loading,
      options: clients,
      hasMore: clients.length === CLIENTS_PAGE_COUNT
    }),
    [loading, searchHandler, clients]
  );
};

export const searchClients = ({
  client: apolloClient
}: {
  client: any;
}) => async (
  input: WbsLabelValueSearchInput
): Promise<WbsLabelValueSearchResult[]> => {
  const { data }: WbsClientLabelSearchQueryResult = await apolloClient.query({
    query: WbsClientLabelSearchDocument,
    variables: {
      date: DateTime.local().toISODate(),
      input: input
    }
  });

  return getClientOptions(data);
};

const transformClient = (
  data: WbsClientLabelSearchQuery | undefined
): FacetOption[] =>
  data?.wbsLabelValueSearch?.clients.items.map((client) => ({
    ...client,
    label: client.displayText,
    key: client.displayText,
    id: client.id || undefined
  })) ?? [];

export const searchClientsByTerm = ({
  client: apolloClient
}: {
  client: any;
}) => async (searchTerm: string): Promise<FacetOption[]> => {
  const { data }: WbsClientLabelSearchQueryResult = await apolloClient.query({
    query: WbsClientLabelSearchDocument,
    variables: {
      date: DateTime.local().toISODate(),
      input: { count: 10, query: searchTerm }
    }
  });

  return transformClient(data);
};

export default useClientLabelSearch;
